<template>
    <b-container fluid class="top-padding small-bottom-padding no-horiz-padding">

        <div v-if='(!isAuthenticated)'>
            Please sign in to view your clipboard and draft boards.
            <login-control prompt="Sign In Now" :show-as-link="false"/>
        </div>

        <h2>Your Clipboard</h2>
        <div class='small-bottom-padding'>Your draft clipboard is a private collection of drafts that only you can see.</div>

        <div class='bottom-padding'>
            <b-button v-if='isAuthenticated' @click='navigateToClipboard()' class='padded-button' size='sm' variant='default'>Clipboard &raquo;</b-button>
        </div>

        <h2>Your Draft Boards</h2>

        <div class='small-bottom-padding'>Draft boards are your own titled collections of drafts which everyone can see.</div>

        <div class='small-bottom-padding' v-for='board in boardsList' :key='board.BoardID'>
            <b-button @click='navigateToBoard(board)' class='padded-button' size='sm' variant='default'><span class='glyphicon glyphicon-book'/> {{ board.Title }} &raquo;</b-button> {{ board.PatternsCount }} <span v-if='board.PatternsCount === 1'> draft</span><span v-if='board.PatternsCount > 1'> drafts</span>
            <b-button class='small-bottom-margin' size='sm' variant='outline-secondary' @click='updateBoardConfirm(board)'><span class="glyphicon glyphicon-pencil"></span> Edit</b-button>
            <b-button class='small-bottom-margin' size='sm' variant='outline-secondary' @click='removeBoardConfirm(board)'><span class="glyphicon glyphicon-remove"></span> Remove</b-button>
        </div>
        <div class='small-bottom-padding' v-if='isAuthenticated'>
            <b-input-group size='sm' class='inputBox'>
                <b-form-input @keydown.native.enter='addNewBoard' v-model='newBoardTitle' placeholder="Enter board title"></b-form-input>
                <template slot="prepend">
                    <b-btn size='sm' variant='primary' @click='addNewBoard' type="button">Add Board</b-btn>
                </template>
            </b-input-group>            
        </div>  

        <help-us-button/>
        <mail-chimp-signup/>
        <b-modal auto-focus-button='cancel' ref='removeBoardConfirmModalRef' title='Remove Board?' ok-title='Remove »' @ok='handleRemoveBoard' cancel-title='Cancel' @cancel='handleRemoveBoardCancel' :no-close-on-backdrop='true' :no-close-on-esc='true'>
            <div class="d-block text-center">
                Would you like to remove your board &quot;{{ boardToRemove.Title }}&quot; ?
            </div>
        </b-modal>
        <b-modal auto-focus-button='ok' ref='updateBoardModalRef' title='Edit Board Title' ok-title='Save »' @ok='updateBoardTitle' cancel-title='Cancel' @cancel='handleUpdateBoardFinished' :no-close-on-backdrop='true' :no-close-on-esc='true'>
            <div class="d-block text-center">
                <b-form-input v-model='updatedBoardTitle'></b-form-input>
            </div>
        </b-modal>
    </b-container>
</template>

<script>
import dataService from '../mixins/dataService.js'
import urlsService from '../mixins/urlsService.js'
import helpUsButton from './HelpUsButton.vue'
import mailChimpSignup from './MailChimpSignup.vue'
import loginControl from './LoginControl.vue'

export default {
    mixins: [dataService, urlsService],
    data: function() {
        return {
            boardsList: [],
            newBoardTitle: '',
            updatedBoardTitle: '',
            boardToRemove: { BoardID: 0, Title: 'placeholder' },
            boardToUpdate: { BoardID: 0, Title: 'placeholder' }
        }
    },
    methods: {
        initializeComponent() {
            if (this.isAuthenticated) {
                this.newBoardTitle = ''
                this.getUserBoards(this.authUser.UserID).then(resp => {
                    // console.log(JSON.stringify(resp.data))
                    this.boardsList = resp.data
                })
            }
        },
        addNewBoard() {
            let title = this.newBoardTitle.trim()
            if (title.length > 3) {
                this.addUserBoard(title, this.authUser.UserID, this.authUser.LoginToken).then(resp => {
                    this.initializeComponent()
                })
            }
        },
        updateBoardTitle() {
            this.updateUserBoard(this.updatedBoardTitle, this.boardToUpdate.BoardID, this.authUser.UserID, this.authUser.LoginToken).then(resp => {
                this.initializeComponent()
                this.showUpdateBoardDialog(false)
            })
        },
        navigateToBoard(board) {
            let sanitizedTitle = this.sanitizeUrlSegment(board.Title)
            this.$router.push({ name: 'DraftSearch', params: { searchType: 'board', bookOrCategoryId: board.BoardID, collectionTitle: sanitizedTitle } })
        },
        navigateToClipboard() {
            this.$router.push({ name: 'DraftSearch', params: { searchType: 'clipboard' } })
        },
        removeBoardConfirm: function(board) {
            // console.log('in removePatternBoardConfirm, board: ' + JSON.stringify(board))
            this.boardToRemove = board
            this.showRemoveBoardDialog(true)
        },
        updateBoardConfirm: function(board) {
            // console.log('in removePatternBoardConfirm, board: ' + JSON.stringify(board))
            this.boardToUpdate = board
            this.updatedBoardTitle = board.Title
            this.showUpdateBoardDialog(true)
        },
        showRemoveBoardDialog(isShow) {
            isShow ? this.$refs.removeBoardConfirmModalRef.show() : this.$refs.removeBoardConfirmModalRef.hide()
        },
        showUpdateBoardDialog(isShow) {
            isShow ? this.$refs.updateBoardModalRef.show() : this.$refs.updateBoardModalRef.hide()
        },
        handleRemoveBoard() {
            this.removeUserBoard(this.boardToRemove.BoardID, this.authUser.UserID, this.authUser.LoginToken).then(resp => {
                this.initializeComponent()
                this.showRemoveBoardDialog(false)
            })
        },
        handleRemoveBoardCancel() {
            this.showRemoveBoardDialog(false)
        },
        handleUpdateBoardFinished() {
            this.showUpdateBoardDialog(false)
        }
    },
    components: {
        helpUsButton: helpUsButton,
        mailChimpSignup: mailChimpSignup,
        loginControl: loginControl
    },
    computed: {
        isSubscriber: function() {
            return this.$store.getters.isSubscriber
        },
        authUser: function() {
            return this.$store.state.authUser
        },
        isAuthenticated: function() {
            let val=this.$store.getters.isAuthenticated
            // console.log('in isAuthenticated, returning ' + JSON.stringify(val))
            return val
        }
    },
    watch: {
        'authUser.UserID': function(val) {
            this.initializeComponent()
        }
    },
    created() {
        this.initializeComponent()
        this.$store.commit('setPageTitle', 'My Draft Boards')
    }
}
</script>

<style scoped>
    .padded-button {
        margin-right: 0.3rem;
        margin-bottom: 0.3rem;
        background-color: #f0f0f0;
    }

    .padded-button:hover {
        background-color: #dfdfdf;
    }
</style>



// WEBPACK FOOTER //
// src/components/MyBoards.vue